import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
    ArrowLeft, KeyRound, MapPin, AlertCircle, CheckCircle2, PlayCircle, PauseCircle, Camera, Video,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import StateBadge from "@/components/StateBadge";
import MediaUpload from "@/components/MediaUpload";
import { TASK_STATE_LABELS, TASK_TYPE_LABELS } from "@/lib/dictionaries";

const DEFAULT_POINTS = [
    { id: "keys_in", label: "Dónde están las llaves" },
    { id: "kitchen", label: "Cocina" },
    { id: "main_bath", label: "Baño principal" },
    { id: "living", label: "Salón" },
    { id: "bedroom", label: "Dormitorios" },
    { id: "trash", label: "Cubo de basura vaciado" },
    { id: "keys_out", label: "Dónde dejas las llaves" },
];

export default function OperatorTaskDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [task, setTask] = useState(null);
    const [points, setPoints] = useState(DEFAULT_POINTS);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [actualMin, setActualMin] = useState("");
    const [notes, setNotes] = useState("");
    const [saving, setSaving] = useState(false);

    const load = () => {
        setLoading(true);
        api.get(`/operator/tasks/${id}`)
            .then((r) => {
                setTask(r.data);
                setActualMin(r.data.actual_minutes || "");
                setNotes(r.data.notes || "");
                // Photo points come from property
                setPoints(r.data.property?.photo_points || DEFAULT_POINTS);
            })
            .catch((e) => setError(formatApiError(e)))
            .finally(() => setLoading(false));
    };
    useEffect(load, [id]); // eslint-disable-line

    if (loading || !task) return <div className="zentra-skeleton h-96" />;
    const media = task.media || [];
    const findMedia = (type, point_id) => media.find((m) => m.type === type && (point_id ? m.point_id === point_id : true));
    const videoBefore = findMedia("video_before");
    const videoAfter = findMedia("video_after");
    const beforePhotos = media.filter((m) => m.type === "photo_before");
    const afterPhotos = media.filter((m) => m.type === "photo_after");
    const beforeReady = Boolean(videoBefore && Number(videoBefore.duration) >= 30 && beforePhotos.length >= 5);
    const afterReady = Boolean(videoAfter && Number(videoAfter.duration) >= 30 && afterPhotos.length >= 5);

    const setTaskState = async (newState) => {
        try {
            await api.patch(`/operator/tasks/${task.id}`, { state: newState });
            load();
        } catch (e) {
            alert(formatApiError(e));
        }
    };

    const saveNotes = async () => {
        setSaving(true);
        try {
            const payload = { notes };
            if (actualMin !== "" && actualMin !== null) payload.actual_minutes = Number(actualMin);
            await api.patch(`/operator/tasks/${task.id}`, payload);
            load();
        } catch (err) {
            alert(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const onMediaUploaded = () => load();

    const beforeMissing = [];
    if (!videoBefore || Number(videoBefore.duration) < 30) beforeMissing.push("Vídeo inicial de 30 segundos");
    if (beforePhotos.length < 5) beforeMissing.push(`${5 - beforePhotos.length} foto(s) de llegada`);
    const afterMissing = [];
    if (!videoAfter || Number(videoAfter.duration) < 30) afterMissing.push("Vídeo final de 30 segundos");
    if (afterPhotos.length < 5) afterMissing.push(`${5 - afterPhotos.length} foto(s) de salida`);

    return (
        <div className="space-y-5" data-testid="op-task-detail">
            <Link to="/operario/tareas" className="text-xs text-zentra-muted hover:text-zentra-ink inline-flex items-center gap-1.5">
                <ArrowLeft size={12} /> Volver al listado
            </Link>

            {/* Header */}
            <div className="zentra-card-static p-5">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                        <p className="text-[11px] font-mono-zentra tracking-wider text-zentra-muted uppercase">
                            {task.property?.code} · {task.scheduled_date}
                            {task.start_time && ` · ${task.start_time}${task.end_time ? `→${task.end_time}` : ""}`}
                        </p>
                        <h2 className="font-display text-2xl font-bold text-zentra-ink tracking-tight mt-1">{task.property?.name}</h2>
                        {task.property?.address && (
                            <p className="text-sm text-zentra-muted mt-1 inline-flex items-center gap-1.5">
                                <MapPin size={13} /> {task.property.address}
                            </p>
                        )}
                        {task.key_access && (
                            <p className="text-xs text-zentra-muted mt-1 inline-flex items-center gap-1.5">
                                <KeyRound size={11} /> {task.key_access}
                            </p>
                        )}
                    </div>
                    <StateBadge value={task.state} dict={TASK_STATE_LABELS} />
                </div>
                <div className="flex items-center gap-2 flex-wrap pt-4 mt-4 border-t border-zentra-border">
                    {task.state === "pending" && (
                        <button onClick={() => setTaskState("in_progress")} disabled={!beforeReady} className="zentra-btn-primary disabled:opacity-50 disabled:cursor-not-allowed" data-testid="op-detail-start">
                            <PlayCircle size={16} /> Empezar limpieza
                        </button>
                    )}
                    {task.state === "in_progress" && (
                        <>
                            <button onClick={() => setTaskState("completed")} disabled={!afterReady} className="zentra-btn-primary disabled:opacity-50 disabled:cursor-not-allowed" data-testid="op-detail-complete">
                                <CheckCircle2 size={16} /> Marcar completada
                            </button>
                            <button onClick={() => setTaskState("pending")} className="zentra-btn-ghost">
                                <PauseCircle size={14} /> Pausar
                            </button>
                        </>
                    )}
                    {task.state === "completed" && (
                        <span className="inline-flex items-center gap-1.5 text-emerald-700 font-semibold text-sm">
                            <CheckCircle2 size={16} /> Tarea completada
                        </span>
                    )}
                </div>
                {!beforeReady && task.state === "pending" && (
                    <div className="mt-3 rounded-md bg-amber-50 border border-amber-200 p-2.5 text-xs text-amber-900">
                        <p className="font-semibold inline-flex items-center gap-1"><AlertCircle size={12} /> Falta antes de iniciar:</p>
                        <ul className="list-disc list-inside mt-1">
                            {beforeMissing.map((m) => <li key={m}>{m}</li>)}
                        </ul>
                    </div>
                )}
                {!afterReady && task.state === "in_progress" && (
                    <div className="mt-3 rounded-md bg-amber-50 border border-amber-200 p-2.5 text-xs text-amber-900">
                        <p className="font-semibold inline-flex items-center gap-1"><AlertCircle size={12} /> Falta antes de completar:</p>
                        <ul className="list-disc list-inside mt-1">
                            {afterMissing.map((m) => <li key={m}>{m}</li>)}
                        </ul>
                    </div>
                )}
            </div>

            {/* Service info */}
            {task.service_type_text && (
                <div className="zentra-card-static p-4">
                    <p className="text-[10px] font-semibold tracking-widest uppercase text-zentra-muted">Tipo de servicio</p>
                    <p className="font-display text-lg font-bold text-zentra-primary mt-1">{task.service_type_text}</p>
                </div>
            )}

            {/* Fixed instructions */}
            {task.property?.fixed_instructions && (
                <div className="zentra-card-static p-4">
                    <p className="text-[10px] font-semibold tracking-widest uppercase text-zentra-accent-strong mb-1.5">Instrucciones fijas de la propiedad</p>
                    <p className="text-sm text-zentra-ink whitespace-pre-wrap font-mono-zentra leading-relaxed">{task.property.fixed_instructions}</p>
                </div>
            )}

            {/* Day instructions */}
            {task.day_instructions && (
                <div className="rounded-md bg-amber-50 border border-amber-200 p-3" data-testid="op-day-instructions">
                    <p className="text-[10px] font-semibold tracking-widest uppercase text-amber-700 mb-1 inline-flex items-center gap-1">
                        <AlertCircle size={11} /> Instrucciones del día
                    </p>
                    <p className="text-xs text-amber-900 whitespace-pre-wrap">{task.day_instructions}</p>
                </div>
            )}

            <Section title={`1 · Llegada · ${beforePhotos.length}/5 fotos`} icon={Video}>
                <p className="text-xs text-zentra-muted mb-3">Obligatorio antes de iniciar: vídeo de al menos 30 segundos y 5 fotos.</p>
                <div className="max-w-md mb-5">
                    <MediaUpload
                        type="video_before"
                        label="Grabar vídeo inicial (mínimo 30 s)"
                        taskId={task.id}
                        existing={videoBefore}
                        onUploaded={onMediaUploaded}
                        onDelete={onMediaUploaded}
                        readOnly={!['pending', 'delayed'].includes(task.state)}
                    />
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                    {points.slice(0, 5).map((pt) => (
                        <div key={`before-${pt.id}`}>
                            <MediaUpload
                                type="photo_before"
                                pointId={pt.id}
                                label={pt.label}
                                taskId={task.id}
                                existing={findMedia("photo_before", pt.id)}
                                onUploaded={onMediaUploaded}
                                onDelete={onMediaUploaded}
                                readOnly={!['pending', 'delayed'].includes(task.state)}
                            />
                            <p className="text-[11px] text-zentra-ink text-center mt-1.5 font-medium">{pt.label}</p>
                        </div>
                    ))}
                </div>
            </Section>

            <Section title={`2 · Salida · ${afterPhotos.length}/5 fotos`} icon={Camera}>
                <p className="text-xs text-zentra-muted mb-3">Disponible tras iniciar. Para completar se exige otro vídeo de 30 segundos y 5 fotos finales.</p>
                <div className="max-w-md mb-5">
                    <MediaUpload
                        type="video_after"
                        label="Grabar vídeo final (mínimo 30 s)"
                        taskId={task.id}
                        existing={videoAfter}
                        onUploaded={onMediaUploaded}
                        onDelete={onMediaUploaded}
                        readOnly={task.state !== "in_progress"}
                    />
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                    {points.slice(0, 5).map((pt) => (
                        <div key={`after-${pt.id}`}>
                            <MediaUpload
                                type="photo_after"
                                pointId={pt.id}
                                label={pt.label}
                                taskId={task.id}
                                existing={findMedia("photo_after", pt.id)}
                                onUploaded={onMediaUploaded}
                                onDelete={onMediaUploaded}
                                readOnly={task.state !== "in_progress"}
                            />
                            <p className="text-[11px] text-zentra-ink text-center mt-1.5 font-medium">{pt.label}</p>
                        </div>
                    ))}
                </div>
            </Section>

            {/* Notes & time */}
            <div className="zentra-card-static p-5">
                <h3 className="font-display font-semibold text-zentra-ink mb-3">Notas y tiempo real</h3>
                <div className="grid md:grid-cols-2 gap-3">
                    <div>
                        <label className="zentra-label">Minutos reales empleados</label>
                        <input type="number" min="0" className="zentra-input" value={actualMin} onChange={(e) => setActualMin(e.target.value)} placeholder={String(task.estimated_minutes || 60)} data-testid="op-detail-actual-min" />
                    </div>
                    <div className="md:col-span-1">
                        <label className="zentra-label">Notas</label>
                        <input className="zentra-input" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Algo a destacar…" data-testid="op-detail-notes" />
                    </div>
                </div>
                <button onClick={saveNotes} className="zentra-btn-ghost mt-3" disabled={saving} data-testid="op-detail-save-notes">
                    {saving ? "Guardando…" : "Guardar"}
                </button>
            </div>
        </div>
    );
}

function Section({ title, icon: Icon, children }) {
    return (
        <div className="zentra-card-static p-5">
            <div className="flex items-center gap-2 mb-3 pb-2 border-b border-zentra-border">
                <Icon size={16} className="text-zentra-primary" />
                <h3 className="font-display font-semibold text-zentra-ink">{title}</h3>
            </div>
            {children}
        </div>
    );
}
